import { useMediaQuery } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import {
  Location,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import RequiredNoteConfirmationDialog from '../../../components/RequiredNoteConfirmationDialog';
import { SideActionItem } from '../../../components/SideActionsList';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  isJourneyPlaceholder,
  Journey,
  JourneyInfo,
  JourneyPlaceholder,
} from '../../../types/journey';
import { formatDateTime } from '../../../utils/date';
import { StatusIcon } from '../../../utils/icon';
import JourneyCancellationDialog from './JourneyCancellationDialog';
import {
  AdHocBadge,
  JourneyEntityStatusBadge,
  JourneyStatusBadge,
} from './JourneyStatusBadges';
import JourneyTemplateActions from './JourneyTemplateActions';
import { JourneyTemplateContext } from './types';
import { isJourneyPending } from './utils';
import { JourneyReliefEventType, JourneyStatus } from '../../../types/core';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const JourneyTemplate = () => {
  const { t } = useLanguage();
  const { journeyId: journeyIdQueryParam } = useParams();
  const {
    getJourneyById,
    getJourneyLog,
    getJourneyNotes,
    submitJourney,
    unsubmitJourney,
    deleteAdhocJourney,
    approveJourney,
    rejectJourney,
  } = useIotsApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { isOperator } = useCurrentUser();

  const isMdUp = useMediaQuery('(min-width:768px)');

  const journeyId = useMemo(() => {
    return typeof journeyIdQueryParam !== 'string' ||
      ['create', 'placeholder'].includes(
        journeyIdQueryParam.toLocaleLowerCase(),
      )
      ? null
      : journeyIdQueryParam;
  }, [journeyIdQueryParam]);

  const {
    data: fetchedJourney = null,
    isFetching: isFetchingJourney,
    error,
    refetch,
  } = useQuery({
    queryKey: ['journey', journeyId ?? ''],
    queryFn: () => getJourneyById(journeyId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: journeyId != null,
  });

  const queryClient = useQueryClient();

  const location = useLocation();

  const { journey, isPlaceholder } = useMemo<{
    journey: JourneyInfo | null;
    isPlaceholder: boolean;
  }>(() => {
    if (journeyId) {
      return { journey: fetchedJourney, isPlaceholder: false };
    }
    // Getting data from the location state
    if (isJourneyPlaceholder(location.state)) {
      const {
        state: {
          contractId,
          operatorId,
          operatorName,
          serviceType,
          journeyStatus,
          entityStatus,
          routeId,
          day,
          time,
          departurePortId,
          arrivalPortId,
        },
      } = location as Location<JourneyPlaceholder>;
      return {
        journey: {
          operatorId,
          operatorName,
          contractId,
          routeId,
          serviceType,
          journeyStatus,
          entityStatus,
          day,
          time,
          departurePortId,
          arrivalPortId,
          journeyReliefEventType: null,
        },
        isPlaceholder: true,
      };
    }

    return { journey: null, isPlaceholder: false };
  }, [journeyId, location, fetchedJourney]);

  const [journeyName, setJourneyName] = useState<string>(() =>
    formatDateTime(journey?.day ?? null, journey?.time ?? null),
  );

  const [journeyIdToReject, setJourneyIdToReject] = useState<string | null>(
    null,
  );

  const [journeyIdToDelete, setJourneyIdToDelete] = useState<string | null>(
    null,
  );

  const setJourneyNameByDateAndTime = (
    date: string | null,
    time: string | null,
  ) => {
    setJourneyName(formatDateTime(date, time));
  };

  useEffect(() => {
    setJourneyName(formatDateTime(journey?.day ?? null, journey?.time ?? null));
  }, [journey]);

  const {
    data: journeyLog = null,
    isFetching: isFetchingJourneyLog,
    error: journeyLogError,
    refetch: refetchJourneyLog,
    isPending: isPendingJourneyLog,
  } = useQuery({
    queryKey: ['journey', journeyId ?? '', 'log'],
    queryFn: () => getJourneyLog(journeyId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: journeyId != null,
  });

  const {
    data: journeyNotes = [],
    isLoading: isLoadingJourneyNotes,
    isFetching: isFetchingJourneyNotes,
    error: notesError,
    refetch: refetchNotes,
  } = useQuery({
    queryKey: ['journey', journeyId ?? '', 'notes'],
    queryFn: () => getJourneyNotes(journeyId ?? ''),
    enabled: journeyId != null,
    // Default ordering descending by date
    select: notes =>
      // As the date is in ISO format, literal string comparison will serve (YYYY-MM-DD...)
      [...notes].sort((a, b) => b.creationDate.localeCompare(a.creationDate)),
  });

  const submitJourneyMutation = useMutation({
    mutationFn: submitJourney,
    onSuccess: passedValidations => {
      if (passedValidations) {
        enqueueSnackbar({
          variant: 'success',
          message: t('content.journey-submission-success'),
        });
      } else {
        // In this case, the error is caused because of the validations failing on this submission.
        enqueueSnackbar({
          variant: 'error',
          message: t(
            'content.journey-submission-error-because-validation-failed',
          ),
        });
      }
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.journey-submission-error')),
      });
      submitJourneyMutation.reset();
    },
    onSettled: () => {
      // There seem to be some kind of problem, so we are going to force a refetching of all data
      // after the operation either succeeds or fail.
      void refetch();
      void refetchJourneyLog();
    },
  });

  const unsubmitJourneyMutation = useMutation({
    mutationFn: unsubmitJourney,
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.journey-unsubmission-success'),
      });
      void refetch();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.journey-unsubmission-error'),
        ),
      });
      submitJourneyMutation.reset();
    },
  });

  const deleteAdhocJourneyMutation = useMutation({
    mutationFn: deleteAdhocJourney,
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.journey-deletion-success'),
      });
      setJourneyIdToDelete(null);
      void disableNavigationGuard(() => navigate('/admin/journeys'));
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.journey-deletion-error')),
      });
      submitJourneyMutation.reset();
    },
  });

  const approvalMutation = useMutation({
    mutationFn: () => approveJourney(journeyId ?? ''),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.journey-approval-success'),
      });
      void refetch();
      void refetchNotes();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.journey-approval-error')),
      });
      approvalMutation.reset();
    },
  });

  const rejectionMutation = useMutation({
    mutationFn: (note: string) => rejectJourney(journeyIdToReject ?? '', note),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.journey-rejection-success'),
      });
      void refetch();
      void refetchNotes();
      setJourneyIdToReject(null);
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.journey-rejection-error')),
      });
      rejectionMutation.reset();
    },
  });

  useQueryErrorNotifier([error, journeyLogError, notesError]);

  const isLoadingJourneyData =
    isFetchingJourney ||
    submitJourneyMutation.isPending ||
    unsubmitJourneyMutation.isPending ||
    deleteAdhocJourneyMutation.isPending;

  const { navigateToJourneyDetails, sidePanelActions, isJourneyLogAllowed } =
    useMemo(() => {
      const detailsUrl = `/admin/journeys/${journeyId ?? (isPlaceholder ? 'placeholder' : 'create')}`;
      const logUrl =
        journeyId || isPlaceholder
          ? `/admin/journeys/${journeyId ?? 'placeholder'}/log`
          : undefined;
      const notesUrl = journeyId
        ? `/admin/journeys/${journeyId}/notes`
        : undefined;

      const navigateToJourneyDetails = () => {
        // For a placeholder, the location state must be preserved
        void navigate(
          detailsUrl,
          location.state
            ? {
                state: location.state as JourneyPlaceholder,
              }
            : undefined,
        );
      };

      let isJourneyLogNeeded = false,
        isJourneyLogAllowed = false;

      if (journey) {
        isJourneyLogNeeded = [
          JourneyStatus.AsScheduled,
          JourneyStatus.Rescheduled,
        ].includes(journey.journeyStatus);

        isJourneyLogAllowed =
          isJourneyLogNeeded &&
          !isJourneyPending(
            JourneyStatus.AsScheduled,
            journey.day,
            journey.time,
          );
      }

      const sidePanelActions: SideActionItem[] = [
        {
          id: 'details',
          label: t('content.journey-details'),
          statusIcon: journey ? StatusIcon.Completed : StatusIcon.Pending,
          url: detailsUrl,
          onClick: event => {
            if (isPlaceholder) {
              event.preventDefault();
              navigateToJourneyDetails();
            }
          },
          isExactUrl: true,
        },
      ];

      if (isJourneyLogNeeded) {
        sidePanelActions.push({
          id: 'log',
          label: t('content.journey-log'),
          statusIcon:
            !journeyLog ||
            !journeyLog.journeyLog ||
            !journeyLog.passengerDetails
              ? StatusIcon.Pending
              : journeyLog.validation.length > 0
                ? StatusIcon.Warning
                : StatusIcon.Completed,
          disabled:
            !journey ||
            (!isJourneyLogAllowed &&
              t('content.journey-log-only-allowed-after-starting')),
          url: logUrl,
          onClick: event => {
            if (isPlaceholder && logUrl) {
              // For a placeholder, the location state must be preserved
              event.preventDefault();
              void navigate(logUrl, {
                state: location.state as JourneyPlaceholder,
              });
            }
          },
        });
      }
      if (journeyId && journeyNotes.length) {
        sidePanelActions.push({
          id: 'notes',
          label: t('content.notes'),
          url: notesUrl,
        });
      }

      return {
        navigateToJourneyDetails,
        sidePanelActions,
        isJourneyLogAllowed,
      };
    }, [
      journeyId,
      isPlaceholder,
      t,
      journey,
      journeyLog,
      journeyNotes.length,
      navigate,
      location.state,
    ]);

  const contextValue = useMemo<JourneyTemplateContext>(() => {
    return {
      journeyId,
      journey,
      setJourneyNameByDateAndTime,
      refetchJourney: () => void refetch(),
      updateContextJourney: updatedJourney => {
        if (journeyId) {
          queryClient.setQueryData<Journey>(['journey', journeyId], {
            ...updatedJourney,
            id: journeyId,
          });
        }
      },
      isFetching: isLoadingJourneyData,
      error,
      navigateToJourneyDetails,
      logManagement: {
        isAllowed: isJourneyLogAllowed,
        journeyLog:
          journeyLog && journeyLog.journeyLog && journeyLog.passengerDetails
            ? {
                journeyLog: journeyLog.journeyLog,
                passengerDetails: journeyLog.passengerDetails,
                validation: journeyLog.validation,
              }
            : null,
        isFetching: isFetchingJourneyLog,
        isPending: isPendingJourneyLog,
        refetch: () => {
          if (journeyId && !isFetchingJourneyLog) {
            void refetchJourneyLog();
          }
        },
      },
      notesManagement: {
        notes: journeyNotes,
        isLoading: isLoadingJourneyNotes,
        error: notesError,
        refetch: () => void refetchNotes(),
      },
    };
  }, [
    journeyId,
    journey,
    isLoadingJourneyData,
    error,
    navigateToJourneyDetails,
    isJourneyLogAllowed,
    journeyLog,
    isFetchingJourneyLog,
    isPendingJourneyLog,
    journeyNotes,
    isLoadingJourneyNotes,
    notesError,
    refetch,
    queryClient,
    refetchJourneyLog,
    refetchNotes,
  ]);

  const [displayingRejectionDialog, setDisplayingRejectionDialog] =
    useState(false);

  return (
    <LeftPanelSectionTemplate
      title={
        <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
          {journeyName ||
            (journeyId ? t('content.edit-journey') : t('content.new-journey'))}
          {journey && (
            <>
              <JourneyStatusBadge
                journeyStatus={journey.journeyStatus}
                reliefEventyType={
                  journey.journeyReliefEventType ??
                  (journeyId ? JourneyReliefEventType.Other : null)
                }
                journeyDay={journey.day}
                journeyTime={journey.time}
                small={!isMdUp}
              />
              <JourneyEntityStatusBadge
                entityStatus={journey.entityStatus}
                small={!isMdUp}
              />
              {!journey.routeId && <AdHocBadge small={!isMdUp} />}
            </>
          )}
        </span>
      }
      isLoading={
        isLoadingJourneyData || isFetchingJourneyLog || isFetchingJourneyNotes
      }
      actions={
        <>
          {journey && (
            <>
              <JourneyTemplateActions
                journeyId={journeyId}
                isAdhoc={!journey.routeId}
                entityStatus={journey.entityStatus}
                isFetching={isLoadingJourneyData}
                isOperator={isOperator}
                onCancelRequest={() => setDisplayingRejectionDialog(true)}
                onSubmitRequest={() => {
                  if (journeyId) {
                    submitJourneyMutation.mutate(journeyId);
                  }
                }}
                onUnsubmitRequest={() => {
                  if (journeyId) {
                    unsubmitJourneyMutation.mutate(journeyId);
                  }
                }}
                onDeleteRequest={() => {
                  if (journeyId) {
                    setJourneyIdToDelete(journeyId);
                  }
                }}
                onApproveRequest={() => {
                  approvalMutation.mutate();
                }}
                onRejectRequest={() => {
                  setJourneyIdToReject(journeyId);
                }}
                hasValidationErrors={
                  !journeyLog?.journeyLog ||
                  (!!journeyLog?.validation && journeyLog.validation.length > 0)
                }
              />
              <RequiredNoteConfirmationDialog
                open={Boolean(journeyIdToReject)}
                title={t('content.reject-journey')}
                noteLabel={t('content.rejection-note-reason')}
                onConfirmRequest={note => {
                  rejectionMutation.mutate(note);
                }}
                onCloseRequest={() => {
                  if (!rejectionMutation.isPending) {
                    setJourneyIdToReject(null);
                  }
                }}
                disabled={rejectionMutation.isPending}
                loading={rejectionMutation.isPending}
              />
            </>
          )}
          <JourneyCancellationDialog
            journey={displayingRejectionDialog ? journey : null}
            journeyId={journeyId}
            onClose={() => setDisplayingRejectionDialog(false)}
            onConfirm={newJourney => {
              setDisplayingRejectionDialog(false);
              if (!journeyId) {
                void navigate(`/admin/journeys/${newJourney.journey.id}/`);
              } else {
                void refetch();
                void refetchNotes();
              }
            }}
          />
          <ConfirmationDialog
            open={Boolean(journeyIdToDelete)}
            loading={deleteAdhocJourneyMutation.isPending}
            title={t('content.journey-deletion-confirmation.title')}
            onConfirm={() => {
              if (journeyIdToDelete) {
                deleteAdhocJourneyMutation.mutate(journeyIdToDelete);
              }
            }}
            onClose={() => {
              setJourneyIdToDelete(null);
            }}
          >
            <ConsequencesList
              action={t('content.journey-deletion-confirmation.action')}
              list={[
                t(
                  'content.journey-deletion-confirmation.bullet-points.will-be-visually-removed',
                ),
              ]}
            />
          </ConfirmationDialog>
        </>
      }
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-journeys')}
      goBackLinkUrl="/admin/journeys"
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default JourneyTemplate;

export const JourneySubPageWrapper = () => (
  <OutletContextReliantRoute<JourneyTemplateContext>
    getOutletContextState={({ journeyId, journey, isFetching }) =>
      journey ? 'ready' : journeyId && isFetching ? 'pending' : 'error'
    }
    errorRedirectionPath="/admin/journeys"
  />
);
